import {
  Box,
  Stack, Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { t } from "i18next";
import {
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";
import {
  // CarouselBox,
  HeroBox
} from "./Explore.styles";
import { FilterDrawer } from "./FilterDrawer.component";
import {
  //ButtonLink,
  CardFilter,
  CarouselSkeleton,
  FiltersCarousel,
  GridMain,
  Link,
  Spinner,
  WidgetDrawer
} from "../../components";
import { BigCarouselCardRestyled } from "../../components/BigCarouselCardRestyle/BigCarouselCardRestyle.component";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { Carousel } from "../../components/Carousel_v2/Carousel.component";
import { CarouselStyle } from "../../components/Carousel_v2/Carousel.styles";
import { CarouselCardRestyle } from "../../components/CarouselCardRestyle/CarouselCardRestyle.component";
import {
  PAGE_NAME,
  SERVICE_TYPES, STATUS_LO
} from "../../consts";
import { UserNavigationContext } from "../../context/UserNavigationContext";
import {
  RelativePath,
  useContentQuery,
  useStructuresQuery,
  useTopicsQuery
} from "../../queries";
import { useAuthStore, useLanguageStore } from "../../stores";
import type {
  LearningObject,
  MetaData
} from "../../types";
import { createEndDate, createStartDate } from "../../utils/general";

// import { createEndDate, createStartDate } from "../../utils/general";


// Utility function to map topicTagIds to translated topic names
function mapTopicIdsToNames(topicTagIds: number[] | undefined, allTopics: { id: number; name: string }[] | undefined): string[] {
  if (!topicTagIds || !allTopics) return [];

  return topicTagIds
    .map(id => allTopics.find(topic => topic.id === id)?.name)
    .filter(Boolean) as string[];
}

export function Explore() {
  // const theme = useTheme();
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  const isLoading = useAuthStore(state => state.isLoading);
  const isAuthenticated = useAuthStore(state => Boolean(state.userData && state.userInfo));

  // const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const language = useLanguageStore(state => state.language)?.id ?? "it";
  const location = useLocation();
  const [selectedTopics, setSelectedTopics] = useState<number[]>([]);
  const [filterQuery, setFilterQuery] = useState("");
  const { data: pageStructure } = useStructuresQuery({
    pageName: PAGE_NAME.EXPLORE_OVERVIEW,
    queryKey: [language]
  });
  //il componente filterDrawer ci restituisce la query da aggiungere nelle api. Ci è stato chiesto di sostiture il query param "learning_object_type"
  // con "course_path_type" SOLO PER ALCUNE API. Nel caso in cui non è richiesto vine sostituito il nuovo query param con il veccchio "learning_object_type"
  let filterQueryParam = filterQuery;
  if (filterQueryParam.includes("course_path_type")) {
    filterQueryParam = filterQueryParam.replace(/course_path_type/g, "learning_object_type");
  }
  const {
    data: allTopics,
    isFetching: topicsAreFetching
  } = useTopicsQuery({ available: true, filterQuery:filterQueryParam }); 
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { state: routerState } = useLocation();
  const { setNavigation } = useContext(UserNavigationContext);

  // Gestione del spinner di login - lo impostiamo a false solo quando l'autenticazione è completata
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      setSpinnerLoginVisible(false);
    }
  }, [isAuthenticated, isLoading, setSpinnerLoginVisible]);

  useEffect(() => {
    if (routerState && routerState.selectedTopics && routerState.selectedTopics.length > 0) {
      const availableTopicIds = allTopics?.filter((topic) => topic.learning_object_associated > 0).map((topic) => topic.id) ?? [];
      setSelectedTopics(routerState.selectedTopics.filter((topicId: number) => availableTopicIds?.includes(topicId)));
    }
  }, [routerState?.selectedTopics, allTopics]);

  const topicsQueryString = selectedTopics.reduce(
    /* the uri arrives with ..topics= */
    (currentTopicsQuery, currentTopic, currentTopicIndex) => {
      if (currentTopicIndex == 0 && currentTopic) {
        return currentTopicsQuery + currentTopic;
      } else {
        return currentTopicsQuery + "&topics=" + currentTopic;
      }
    },
    ""
  ) + (filterQuery ? `&${filterQuery}` : "");

  useEffect(() => {
    setNavigation && setNavigation([]);
    setNavigation && setNavigation(["/esplora"]);
  }, [location.pathname]);


  //é stato eliminato lo usememo perchè al cambio lingua non veniva aggiornata correttamente la label widgetContinueLearning
  //TODO : se non ci saranno problemi possiamo eliminare il commento nel post restyle
  const relativePaths = pageStructure?.relativePaths ?? [];

  const topCarousel =
  relativePaths.find(
    ({ serviceType }) => serviceType === SERVICE_TYPES.sliderTopContentExplore
  ) ?? null;

  const otherCarousels = relativePaths.filter(
    ({ serviceType }) => serviceType !== SERVICE_TYPES.sliderTopContentExplore
  );

  const lastCarouselId = otherCarousels.length
    ? Math.max(...otherCarousels.map(carousel => carousel.id))
    : 0;

  const ongoingCarousel: RelativePath = {
    id: lastCarouselId + 1,
    title: t("widgetContinueLearning"),
    apiPath:
    "/learning-catalogue/{corporateId}/{initiativeId}/learnerOnGoingLearningObjects?pageNumber={pageNumber}&pageSize={pageSize}&topics={topics}",
    serviceType: "visCarOnGoingLearningObjects",
    premium: false,
    isAttribute: false
  };

  const carousels = [ongoingCarousel, ...otherCarousels];

  return (
    <GridMain component="div">
      <WidgetDrawer />
      {
        topCarousel ? (
          <ExploreBigCarousel
            key={ `explore-big-carousel--${topCarousel.id}--${language}` }
            relativePath={ topCarousel }
            allTopics={ allTopics }
          />
        ) : null
      }
      <FilterDrawer
        onChange={ (q) => setFilterQuery(q.toString()) }
        nameKeyFilterStorage="explore_filters"></FilterDrawer>
      {
        topicsAreFetching ? (
          <Spinner width="100%" />
        ) : (
          <FiltersCarousel label={ "filters-carousel" }>
            {
              allTopics?.map((topic) => (
                (topic.learning_object_associated > 0) ? (
                  <CardFilter
                    key={ `explore-filter--filter-${topic.id}` }
                    coverPublicURL={ topic.url.public_url }
                    onClickhandler={ () => {
                      const currentSelectedCategories = [...selectedTopics];
                      if (selectedTopics.includes(topic.id)) {
                        currentSelectedCategories.splice(
                          currentSelectedCategories.indexOf(topic.id),
                          1
                        );
                      } else {
                        currentSelectedCategories.push(topic.id);
                        currentSelectedCategories.sort();
                      }
                      setSelectedTopics(currentSelectedCategories);
                    } }
                    title={ topic.name }
                    selected={ selectedTopics.includes(topic.id) }
                  />
                ) : null
              ))
            }
          </FiltersCarousel>
        )
      }
      <Stack
        tabIndex={ -1 }
        minHeight={ "calc(100vh - 550px)" }
        ref={ containerRef }
        width={ "100%" }
        gap={ "3rem" }
      >
        {
          carousels.map((carousel, index) => (
            <ExploreCarousel
              key={ `explore-carousel--${index}--${language}` }
              reference={ index === carousels.length - 1 ? itemsRef : null }
              relativePath={ carousel }
              topicsQueryString={ topicsQueryString }
              hideProgressBar={ carousel.serviceType !== "visCarOnGoingLearningObjects" }
              allTopics={ allTopics }
            />
          ))
        }
      </Stack>
    </GridMain>
  );
}

function ExploreBigCarousel({
  relativePath,
  allTopics
}: {
  relativePath: RelativePath
  allTopics: { id: number; name: string }[] | undefined
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const language = useLanguageStore(state => state.language)?.id ?? "it";

  const {
    data: carouselCards,
    isFetching,
    isError,
    error
  } = useContentQuery<
    & Record<string, LearningObject[]>
    & { metadata: MetaData }
  >({
    path: relativePath.apiPath,
    queryKey: [relativePath.title, language],
    topics: ""
  });

  const responseKey = carouselCards ? (Object.keys(carouselCards).find((key) => key !== "metadata") ?? "") : "";
  const carouselData = (carouselCards && responseKey) ? carouselCards[responseKey] : [];

  function handleMobile() {
    if (!isMobile) {
      return (
        <Carousel
          hasPagination={ true }
          hasPaginationSweeperBullets={ false }
          isBoxShadowEnabled={ false }
          carouselData={ carouselData }
          pauseOnMouseEnter={ true }
          title={ "bigcarousel" }
          slidesPerView={ 1 }
          centeredSlides={ true }
          renderElementBigcarouselGridView={ (element) => {
            return (
              <CarouselCardRestyle
                canAccessNow={ element.canAccessNow }
                enrollType={ element.enrollType }
                booked={ element.status !== STATUS_LO.N && element.status !== STATUS_LO.T }
                categories={ mapTopicIdsToNames(element.topicTagIds, allTopics) }
                coverPublicURL={ element?.coverPublicURL?.toString() || "" }
                coverVideoPublicURL={ element.videoPublicURL }
                disabled={ false }
                duration={ element.duration }
                ecm_specialization={ element.ecm_specialization }
                ecmRegistration={ element.ecmRegistration }
                ecmType={ element.ecmType }
                editionNumber={ element.editionNumber }
                endDate={ createEndDate(element) }
                expirationDate={ element.expirationDate }
                id={ element.id }
                isEditorial={ element.isEditorial }
                isAutoSubscribeEnable={ element.isAutoSubscribeEnable }
                isFavourite={ element.isFavourite }
                isMandatory={ element.isMandatory }
                isSurvey={ element.isSurvey }
                isTest={ element.isTest }
                isToj={ element.isToj }
                key={ `carousel-explore-zoom-${relativePath.title.replaceAll(" ", "-")}--card-${element.id}--${language}` }
                learningObjectType={ element.learningObjectType }
                learningObjectTypology={ element.learningObjectTypology }
                percentageOfCompletion={ element.percentageOfCompletion }
                shortDescription={ element.shortDescription }
                startDate={ createStartDate(element) }
                status={ element.status || "N" }
                title={ element.title }
                isLinkRelative={ element.isLinkRelative }
                externalFileUrl={ element.externalFileUrl }
                link={ element.link }
                cardType={ "small" }
                attributes={ element.attributes }
              />
            );
          } }
          renderElement={ (card) => {
            return (
              <BigCarouselCardRestyled
                key={ `explore-hero--card-${card.id}` }
                coverPublicURL={ card.coverPublicURL }
                videoPublicURL={ card.videoPublicURL }
                duration={ card.duration }
                id={ card.id }
                learningObjectTypology={ card.learningObjectTypology }
                shortDescription={ card.shortDescription }
                simple={ true }
                categories={ mapTopicIdsToNames(card.topicTagIds, allTopics) }
                title={ card.title }
                canAccessNow={ card.canAccessNow || true }
                percentageOfCompletion={ card.percentageOfCompletion?.slice(0, -1) || "0" }
                learningObjectType={ card.learningObjectType }
                isExpired={ card.isExpired }
                isMandatory={ card.isMandatory }
                isEditorial={ card.isEditorial }
                expirationDate={ card.expirationDate }
                enrollType={ card.enrollType }
                status={ card.status || "N" }
                endDate={ createEndDate(card) }
                startDate={ createStartDate(card) }
                isToj={ card.isToj }
                isHighLightEdition={ card?.editions?.[0]?.isHighlightEdition ?? false }
                labelMoreInfo={ true }
                link={ card.link }
                isLinkRelative={ card.isLinkRelative }
                externalFileUrl={ card.externalFileUrl }
                attributes={ card.attributes }
                isSuggestedAI={ card.isSuggestedAI }
                isSuggestedHR={ card.isSuggestedHR }
                isSuggestedManager={ card.isSuggestedManager }
                badge={ card.badge }
                certificate={ card.certificate }
                ecmRegistration={ card.ecmRegistration }
                ecm_specialization={ card.ecm_specialization }
              />
            );
          } }
        />
      );
    }
    return (
      <Carousel
        hasPagination={ true }
        hasPaginationSweeperBullets={ false }
        isBoxShadowEnabled={ false }
        carouselData={ carouselData }
        pauseOnMouseEnter={ true }
        title={ "bigcarousel" }
        slidesPerView={ 1 }
        centeredSlides={ true }
        renderElement={ (element) => {
          return (
            <CarouselCardRestyle
              canAccessNow={ element.canAccessNow }
              enrollType={ element.enrollType }
              booked={ element.status !== STATUS_LO.N && element.status !== STATUS_LO.T }
              categories={ mapTopicIdsToNames(element.topicTagIds, allTopics) }
              coverPublicURL={ element?.coverPublicURL?.toString() || "" }
              coverVideoPublicURL={ element.videoPublicURL }
              disabled={ false }
              duration={ element.duration }
              ecm_specialization={ element.ecm_specialization }
              ecmRegistration={ element.ecmRegistration }
              ecmType={ element.ecmType }
              editionNumber={ element.editionNumber }
              endDate={ createEndDate(element) }
              expirationDate={ element.expirationDate }
              id={ element.id }
              isAutoSubscribeEnable={ element.isAutoSubscribeEnable }
              isFavourite={ element.isFavourite }
              isMandatory={ element.isMandatory }
              isSurvey={ element.isSurvey }
              isEditorial={ element.isEditorial }
              isTest={ element.isTest }
              isToj={ element.isToj }
              key={ `carousel-explore-zoom-${relativePath.title.replaceAll(" ", "-")}--card-${element.id}--${language}` }
              learningObjectType={ element.learningObjectType }
              learningObjectTypology={ element.learningObjectTypology }
              percentageOfCompletion={ element.percentageOfCompletion }
              shortDescription={ element.shortDescription }
              startDate={ createStartDate(element) }
              status={ element.status || "N" }
              title={ element.title }
              isLinkRelative={ element.isLinkRelative }
              externalFileUrl={ element.externalFileUrl }
              link={ element.link }
              cardType={ "small" }
              attributes={ element.attributes }
            />
          );
        } }
      />
    );
  }

  // Se c'è un errore, non mostrare niente invece dello spinner infinito
  if (isError) {
    console.warn("Errore nel caricamento del big carousel:", error);
    return null;
  }

  return isFetching ? (
    <Spinner
      // similar height of BigCarouselCard
      minHeight={ isMobile ? "366px" : "450px" }
      size={ 90 }
      width="100%"
    />
  ) : (
    (carouselCards && carouselCards.metadata?.totalElements > 0) ? (
      <HeroBox>
        { handleMobile() }
      </HeroBox>
    ) : null
  );
}

function ExploreCarousel({
  relativePath,
  // reference,
  topicsQueryString,
  href=undefined,
  hideProgressBar = false,
  allTopics
}: {
  relativePath: RelativePath
  reference: RefObject<HTMLDivElement> | null
  topicsQueryString: string,
  href?:string,
  hideProgressBar?: boolean,
  allTopics?: { id: number; name: string }[]
}) {
  const{  state:routerState } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const language = useLanguageStore(state => state.language)?.id ?? "it";

  //il componente filterDrawer ci restituisce la query da aggiungere nelle api. Ci è stato chiesto di sostiture il query param "learning_object_type"
  // con "course_path_type" SOLO PER ALCUNE API(api che hanno all'interno "attribute-learning-objects"). Nel caso in cui non è richiesto vine sostituito il nuovo query param con il veccchio "learning_object_type"
  let topicQueryStringChecked = topicsQueryString;
  if (!relativePath.apiPath.includes("attribute-learning-objects")) {
    topicQueryStringChecked = topicQueryStringChecked.replace(/course_path_type/g, "learning_object_type");
  }
 
  const {
    data: carouselCards,
    isFetching,
    isError,
    error
  } = useContentQuery<
    & Record<string, LearningObject[]>
    & { metadata: MetaData }
  >({
    path: relativePath.apiPath,
    queryKey: [relativePath.title, topicQueryStringChecked, language],
    topics: topicQueryStringChecked
  });

  const handleForYouPrefix = () => {
    if(relativePath?.isAttribute || relativePath?.serviceType === "trainingMandatory"){
      return t("for_you") + " - ";
    }
    return "";
  };

  const title = (relativePath?.serviceType &&
      (relativePath?.serviceType !== "visCarCategory") &&
      (relativePath?.serviceType !== "visCarOnGoingLearningObjects")
  ) ?
    handleForYouPrefix() + t(relativePath?.serviceType) : handleForYouPrefix() + relativePath?.title;

  // const isPremium = relativePath?.premium ?? false;

  const responseKey = carouselCards ? (Object.keys(carouselCards).find((key) => key !== "metadata") ?? "") : "";
  const carouselData = (carouselCards && responseKey) ? carouselCards[responseKey] : [];
  const label = relativePath.title?.trim().length ? relativePath.title : `${relativePath.id}`;
  const totalElements = carouselCards?.metadata?.totalElements ?? 0;

  // Se c'è un errore, non mostrare niente invece dello skeleton infinito
  if (isError) {
    console.warn("Errore nel caricamento del carosello " + title + ":", error);
    return null;
  }

  return (
    (isFetching ? <CarouselSkeleton /> :
      (Number(carouselCards?.metadata?.totalElements) > 0) ? (
      //*** NEW ELEMENTs ***//
        <CarouselStyle aria-label={ title } className="carouselContainer">
          <Box sx={ {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          } }>
            <Typography fontSize={ "1.5rem" } fontWeight={ 700 } lineHeight={ "2.1rem" }>{ title }</Typography>
            <Link
              href={ href != undefined ? href : `/esplora/categories/${label}` }
              state={ {
                ...routerState,
                titleCards:title,
                topicQueryStringChecked,
                relativePath
              } }
            >
              <ButtonDigited
                tabIndex={ -1 }
                disabled={ isFetching }
                typology="tertiary"
                buttonSize="md"
                text={ `${t("discover_all")}  (${totalElements})` }
              ></ButtonDigited>
            </Link>
          </Box>
          <Carousel
            centeredSlides={ isMobile ? true : false }
            carouselData={ carouselData }
            slidesPerView={ isMobile ? 1 : "auto" }
            spaceBetween={ isMobile ? 1 : 11.2 }
            loopSlides={ false }
            hasPagination={ false }
            hideSweeperBulletsRight={ true }
            hasPaginationSweeperBullets={ false }
            title={ title + "-carousel-explore" }
            pauseOnMouseEnter={ true }
            autoplay={ false }
            renderElement={ (element) => {
              return (
                <CarouselCardRestyle
                  loTag={ element.lo_Tag }
                  link={ element.link }
                  canAccessNow={ element.canAccessNow }
                  enrollType={ element.enrollType }
                  booked={ element.status !== STATUS_LO.N && element.status !== STATUS_LO.T }
                  categories={ mapTopicIdsToNames(element.topicTagIds, allTopics) }
                  coverPublicURL={ element?.coverPublicURL?.toString() || "" }
                  coverVideoPublicURL={ element.videoPublicURL }
                  disabled={ false }
                  duration={ element.duration }
                  ecm_specialization={ element.ecm_specialization }
                  ecmRegistration={ element.ecmRegistration }
                  ecmType={ element.ecmType }
                  editionNumber={ element.editionNumber }
                  endDate={ createEndDate(element) }
                  expirationDate={ element.expirationDate }
                  externalFileUrl={ element.externalFileUrl }
                  id={ element.id }
                  isAutoSubscribeEnable={ element.isAutoSubscribeEnable }
                  isFavourite={ element.isFavourite }
                  isEditorial={ element.isEditorial }
                  isLinkRelative={ element.isLinkRelative }
                  isMandatory={ element.isMandatory }
                  isTest={ element.isTest }
                  isSurvey={ element.isSurvey }
                  isToj={ element.isToj }
                  key={ `carousel-explore-zoom-${relativePath.title.replaceAll(" ", "-")}--card-${element.id}--${language}` }
                  learningObjectType={ element.learningObjectType }
                  learningObjectTypology={ element.learningObjectTypology }
                  percentageOfCompletion={ element.percentageOfCompletion }
                  shortDescription={ element.shortDescription }
                  startDate={ createStartDate(element) }
                  status={ element.status || "N" }
                  title={ element.title }
                  cardType={ "small" }
                  attributes={ element.attributes }
                  hideProgressBar={ hideProgressBar }
                />
              );
            }
            }
          />

        </CarouselStyle>

      ) : null
    )
  );
}
